<template>
  <v-tabs
    v-model="tab"
    height="60"
    slider-color="primary"
    color="primary"
    class="mt-10 mb-4"
    show-arrows
  >
    <v-tab
      v-for="item in tabs"
      :key="item.value"
      :text="item.title"
      :value="item.value"
    />
  </v-tabs>

  <v-tabs-window v-model="tab">
    <v-tabs-window-item
      value="memberships"
      :transition="false"
      :reverse-transition="false"
    >
      <GeneratedPersonMemberships
        :key="personId"
        :person-id="personId"
        mode="member"
      />
    </v-tabs-window-item>

    <v-tabs-window-item
      v-if="!isJunior"
      value="damage-waivers"
      :transition="false"
      :reverse-transition="false"
    >
      <GeneratedPersonDamageWaivers
        :key="personId"
        :person-id="personId"
        mode="member"
      />
    </v-tabs-window-item>

    <v-tabs-window-item
      v-if="isSelf"
      value="balances"
      :transition="false"
      :reverse-transition="false"
    >
      <v-card elevation="0" title="Balances" class="pa-6">
        <BalancesTable />
      </v-card>
    </v-tabs-window-item>

    <v-tabs-window-item
      value="cards"
      :transition="false"
      :reverse-transition="false"
    >
      <GeneratedPersonCards
        :key="personId"
        :person-id="personId"
        mode="member"
      />
    </v-tabs-window-item>

    <v-tabs-window-item
      value="classes"
      :transition="false"
      :reverse-transition="false"
    >
      <PersonClassesTab :key="personId" />
    </v-tabs-window-item>

    <v-tabs-window-item
      value="ratings"
      :transition="false"
      :reverse-transition="false"
    >
      <v-card elevation="0" title="Ratings" class="pa-6">
        <PersonRatingGridPanel
          :key="personId"
          :person-id="personId"
          mode="member"
        />
      </v-card>
    </v-tabs-window-item>

    <v-tabs-window-item
      value="signouts"
      :transition="false"
      :reverse-transition="false"
    >
      <GeneratedPersonSignouts
        :key="personId"
        :person-id="personId"
        mode="member"
      />
    </v-tabs-window-item>

    <v-tabs-window-item
      v-if="!isJunior"
      value="donations"
      :transition="false"
      :reverse-transition="false"
    >
      <GeneratedPersonDonations
        :key="personId"
        :person-id="personId"
        mode="member"
      />
    </v-tabs-window-item>

    <v-tabs-window-item
      value="emergency-contacts"
      :transition="false"
      :reverse-transition="false"
    >
      <v-card elevation="0" title="Emergency Contacts" class="pa-6">
        <v-alert v-if="contactsError" type="error" variant="tonal">
          {{ contactsError }}
        </v-alert>
        <v-progress-linear v-else-if="contactsLoading" indeterminate />
        <p v-else-if="!contacts.length" class="text-medium-emphasis">
          No emergency contacts on file.
        </p>
        <v-list v-else lines="two" data-testid="emergency-contacts">
          <v-list-item
            v-for="contact in contacts"
            :key="contact.person.personId"
            :title="contact.person.name"
          >
            <v-list-item-subtitle v-if="contact.person.email">
              {{ contact.person.email }}
            </v-list-item-subtitle>
            <v-list-item-subtitle
              v-for="phone in contact.phones"
              :key="phone.association.assignId"
            >
              {{ phone.name }}: {{ formatPhoneNumber(phone.phone.phoneNumber) }}
              {{ phone.phone.phoneType }}
            </v-list-item-subtitle>
          </v-list-item>
        </v-list>
      </v-card>
    </v-tabs-window-item>
  </v-tabs-window>
</template>

<script lang="ts" setup>
import type { PersonMember } from "../../../cbi-generator/generated/dto/PersonMember";
import type { MemberEmergencyContact } from "../../../cbi-generator/generated/dto/MemberEmergencyContact";
import { usePersonService } from "../../../cbi-generator/frontend/composables/usePersonFrontendService";
import { useUtility } from "@/composables/useUtility";
import { useDirtyFlag } from "@/composables/useDirtyFlag";
import { errorMessage } from "@/composables/generated/contracts";
import GeneratedPersonMemberships from "@/components/person/GeneratedPersonMemberships.vue";
import GeneratedPersonDamageWaivers from "@/components/person/GeneratedPersonDamageWaivers.vue";
import GeneratedPersonCards from "@/components/person/GeneratedPersonCards.vue";
import GeneratedPersonSignouts from "@/components/person/GeneratedPersonSignouts.vue";
import GeneratedPersonDonations from "@/components/person/GeneratedPersonDonations.vue";
import PersonRatingGridPanel from "@/components/person/PersonRatingGridPanel.vue";
import PersonClassesTab from "@/components/person/tabs/PersonClassesTab.vue";
import BalancesTable from "@/components/tables/balances/BalancesTable.vue";

const props = defineProps<{
  person: PersonMember;
  isSelf: boolean;
  /** Junior members can't hold damage waivers or donate, so those tabs are hidden. */
  isJunior?: boolean;
}>();
const personService = usePersonService();
const { formatPhoneNumber } = useUtility();

const personId = computed(() => props.person.personId);
provide(
  "person",
  computed(() => props.person),
);

provide("membershipDataState", useDirtyFlag());
provide("cardDataState", useDirtyFlag());
provide("classDataState", useDirtyFlag());
provide("ratingDataState", useDirtyFlag());
provide("donationDataState", useDirtyFlag());

const tab = shallowRef("memberships");
const tabs = computed(() =>
  [
    { title: "Memberships", value: "memberships" },
    { title: "Damage Waivers", value: "damage-waivers", adultOnly: true },
    { title: "Balances", value: "balances", selfOnly: true },
    { title: "Cards", value: "cards" },
    { title: "Classes", value: "classes" },
    { title: "Ratings", value: "ratings" },
    { title: "Signouts", value: "signouts" },
    { title: "Donations", value: "donations", adultOnly: true },
    { title: "Emergency Contacts", value: "emergency-contacts" },
  ].filter(
    (t) => (!t.selfOnly || props.isSelf) && (!t.adultOnly || !props.isJunior),
  ),
);
watch(tabs, (list) => {
  if (!list.some((t) => t.value === tab.value)) tab.value = "memberships";
});

const contacts = shallowRef<MemberEmergencyContact[]>([]);
const contactsLoading = ref(false);
const contactsError = ref("");
let contactsVersion = 0;
onScopeDispose(() => ++contactsVersion);

watch(
  () => props.person,
  async (person) => {
    const version = ++contactsVersion;
    contacts.value = [];
    contactsError.value = "";
    contactsLoading.value = true;
    try {
      const loaded = await personService.getProfileMember({
        personId: person.personId,
      });
      if (version === contactsVersion)
        contacts.value = loaded.emergencyContactDetails;
    } catch (cause) {
      if (version === contactsVersion)
        contactsError.value = errorMessage(cause);
    } finally {
      if (version === contactsVersion) contactsLoading.value = false;
    }
  },
  { immediate: true },
);
</script>
